import { useState, useContext, useEffect } from "react";
import { Upload, Form } from "antd";
import type { UploadProps, RcFile } from "antd/es/upload";
import type { UploadFile } from "antd/es/upload/interface";
import { CloudUploadOutlined } from "@ant-design/icons";

import styles from "./Task.module.css";

import { TaskFormContext } from "../../../providers/TaskFormProvider";
import { TaskFormContextType } from "../../../providers/interfaces";
import {
  isFileSizeValid,
  isPDF,
  isDOC,
  isDOCX,
  isXLS,
  isXLSX,
} from "../../../components/Forms/utils";

import { onCustomUploadFile } from "../../UploadItem/customRequest";

import { message } from "antd";

const UploadSection = ({ required = true, initialFileName }: { required?: boolean; initialFileName?: string }) => {
  const {
    addAnonymizationDocument,
    addAnonymizationDocumentError,
  } = useContext(TaskFormContext) as TaskFormContextType;

  const [fileList, setFileList] = useState<UploadFile<RcFile>[]>([]);

  const form = Form.useFormInstance();

  useEffect(() => {
    if (initialFileName) {
      const initial: UploadFile<RcFile> = {
        uid: "-1",
        name: initialFileName,
        status: "done",
      };
      setFileList([initial]);
    }
  }, [initialFileName]);

  const beforeUploadAR = (file: RcFile) => {
    if (!isFileSizeValid(file)) {
      addAnonymizationDocumentError(
        "File uploaded is too large. Maximum allowed is 300 MB.",
        "AR"
      );
      message.error("File uploaded is too large. Maximum allowed is 300 MB.");
      return Upload.LIST_IGNORE;
    }
    
    const isSupportedFile = isPDF(file) || isDOC(file) || isDOCX(file) || isXLS(file) || isXLSX(file);
    if (!isSupportedFile) {
      addAnonymizationDocumentError(
        "Unsupported file type. Please upload PDF, DOC, DOCX, XLS, or XLSX files.",
        "AR"
      );
      message.error("Unsupported file type. Please upload PDF, DOC, DOCX, XLS, or XLSX files.");
      return Upload.LIST_IGNORE;
    }
    
    return true;
  };

  const handleChange: UploadProps["onChange"] = (info) => {
    if (info.file.status === "done") {
      addAnonymizationDocument(info.file.name, "AR");
      setFileList([info.file]);
      form?.setFieldsValue?.({ arxiu: info.file.name });
    } else if (info.file.status === "error") {
      addAnonymizationDocumentError(
        `${info.file.name} file upload failed`,
        "AR"
      );
    }
  };

  const handleRemove = () => {
    setFileList([]);
    form?.setFieldsValue?.({ arxiu: undefined });
    return true;
  };

  return (
    <div className={styles.uploadBlock}>
      <Form.Item
        label={<strong>Arxiu</strong>}
        name="arxiu"
        rules={required && fileList.length === 0 ? [{ required: true, message: "Aquest camp és obligatori, si us plau Completar" }] : []}
      >
        <Upload.Dragger
          customRequest={onCustomUploadFile}
          beforeUpload={beforeUploadAR}
          onChange={handleChange}
          onRemove={handleRemove}
          // fileList={fileList}
          accept=".pdf,.doc,.docx,.xls,.xlsx"
          maxCount={1}
          onDrop={(e) => {
            const droppedFiles = Array.from(e.dataTransfer.files);
            droppedFiles.forEach((file) => {
              const isSupportedFile =
                isPDF(file as RcFile) ||
                isDOC(file as RcFile) ||
                isDOCX(file as RcFile) ||
                isXLS(file as RcFile) ||
                isXLSX(file as RcFile);
              if (!isSupportedFile) {
                addAnonymizationDocumentError(
                  "Anonymization report only supports PDF, DOC, DOCX, XLS, or XLSX files",
                  "AR"
                );
                message.error("Només s'admeten fitxers PDF, DOC, DOCX, XLS, o XLSX");
              }
            });
          }}
        >
          <p className="ant-upload-drag-icon">
            <CloudUploadOutlined className={styles.uploadIcon} />
          </p>
          <p className="ant-upload-hint">
            Arrossega o selecciona un fitxer (DOC, XLS, PDF, màx. 10 MB). L'anonimització pot trigar uns minuts.
          </p>
        </Upload.Dragger>
      </Form.Item>
    </div>
  );
};

export default UploadSection;
